import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ProductionDeclarationOrderBanner } from '@/features/production-declaration/components/shared/production-declaration-order-banner';
import { ProductionDeclarationMaterialsList } from '@/features/production-declaration/components/statement/production-declaration-materials-list';
import { ProductionDeclarationCompletenessAlert } from '@/features/production-declaration/components/submission/production-declaration-completeness-alert';
import { ProductionDeclarationSubmitDialog } from '@/features/production-declaration/components/submission/production-declaration-submit-dialog';
import { useProductionDeclarationAction } from '@/features/production-declaration/hooks/use-production-declaration-action';
import { useProductionDeclarationCompleteness } from '@/features/production-declaration/hooks/use-production-declaration-completeness';
import { ProductionDeclarationContentAction } from '@/features/production-declaration/production-declaration-content-action';
import { useGetProductionDeclaration } from '@/services/apis/queries/production-declaration/use-get-production-declaration';
import { useGetProductionDeclarationMaterials } from '@/services/apis/queries/production-declaration/use-get-production-declaration-materials';
import { ProductionDeclarationStatusCode } from '@/types/core/purchase-order/production-declaration-status-code';

export function ProductionDeclarationStatementPage() {
  const { id } = useParams({ strict: false });
  const { token } = useSearch({ strict: false });
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('productionDeclaration');
  const productionDeclarationId = `${id}`;
  const { data } = useGetProductionDeclaration(productionDeclarationId, token);
  const { data: materials = [], isLoading: isMaterialsLoading } =
    useGetProductionDeclarationMaterials(productionDeclarationId, token);
  const { visibility, handleSubmit, isPending } =
    useProductionDeclarationAction({
      statusCode: data.status.code,
      productionDeclarationId,
      token,
      materials,
    });
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [showCompletenessErrors, setShowCompletenessErrors] = useState(false);
  const { incompleteMaterials, isComplete } =
    useProductionDeclarationCompleteness({
      productionDeclarationId,
      token,
    });

  const handleValidateClick = () => {
    if (!isComplete) {
      setShowCompletenessErrors(true);
      return;
    }
    setShowCompletenessErrors(false);
    setSubmitDialogOpen(true);
  };

  const handleSubmitConfirm = () => {
    handleSubmit({
      onSuccess: () => {
        setSubmitDialogOpen(false);
        navigate({
          params: { id: productionDeclarationId },
          replace: true,
          search: { token },
          to: '/production-declarations/$id/submission',
        });
      },
    });
  };

  const hasVisibleCompletenessErrors =
    showCompletenessErrors && incompleteMaterials.length > 0;

  if (
    !isAuthenticated &&
    data.status.code !== ProductionDeclarationStatusCode.Pending
  ) {
    return (
      <Navigate
        params={{ id: productionDeclarationId }}
        replace
        search={{ token }}
        to='/production-declarations/$id/submission'
      />
    );
  }

  return (
    <div className='space-y-10'>
      <div className='space-y-3'>
        {isAuthenticated ? (
          <Link
            className='inline-flex items-center gap-2 text-declaration-foreground text-sm transition-colors hover:text-declaration-foreground-muted'
            params={{ id: `${data.purchaseOrderId}` }}
            to='/purchase-orders/$id'
          >
            <ChevronLeft aria-hidden className='size-5' />
            {t('statement.backToPurchaseOrder')}
          </Link>
        ) : null}
        <h1 className='font-semibold text-4xl text-declaration-foreground tracking-tight'>
          {t('statement.title')}
        </h1>
      </div>
      <ProductionDeclarationOrderBanner
        actionDescription={
          visibility.showApprove || visibility.showRefuse
            ? t('actions.emailNotice')
            : undefined
        }
        actionDisabled={isPending || hasVisibleCompletenessErrors}
        actionLabel={visibility.showSubmit ? t('actions.submit') : undefined}
        actionOnClick={visibility.showSubmit ? handleValidateClick : undefined}
        actionSlot={
          isAuthenticated &&
          data.status.code === ProductionDeclarationStatusCode.Submitted ? (
            <ProductionDeclarationContentAction
              declarationToken={data.token}
              materials={materials}
              productionDeclarationId={productionDeclarationId}
              purchaseOrderId={data.purchaseOrderId}
              purchaseOrderName={data.purchaseOrderName}
              statusCode={data.status.code}
              supplierContacts={data.supplierContacts}
              supplierName={data.supplierName}
              token={token}
            />
          ) : undefined
        }
        productionDeclarationId={productionDeclarationId}
        purchaseOrderName={data.purchaseOrderName}
        supplierName={data.supplierName}
        token={token}
      />
      {showCompletenessErrors ? (
        <ProductionDeclarationCompletenessAlert
          incompleteMaterials={incompleteMaterials}
        />
      ) : null}
      <ProductionDeclarationMaterialsList
        isLoading={isMaterialsLoading}
        materials={materials}
      />
      <ProductionDeclarationSubmitDialog
        isPending={isPending}
        onConfirm={handleSubmitConfirm}
        onOpenChange={setSubmitDialogOpen}
        open={submitDialogOpen}
        supplierName={data.supplierName ?? ''}
      />
    </div>
  );
}
